import {
  clearDemoSession,
  DEMO_ACTIVITY_EVENT,
  isDemoUserId,
  readDemoSession,
  saveDemoSession
} from "./demoSession";
import { signOutWithBarrier, waitForPendingSignOut } from "./authTransition";
import type { DemoSession } from "./demoSession";
import { isRecord } from "../types/data";
import type { JsonValue } from "../types/data";

interface DemoRequestAuth { currentUser: { uid: string; getIdToken: (forceRefresh?: boolean) => Promise<string> } | null; }
interface DemoAuth extends DemoRequestAuth {
  signInWithCustomToken: (token: string) => Promise<{ user: { uid: string } | null }>;
  signOut: () => Promise<void>;
}
interface DemoApiPayload extends Partial<DemoSession> { ok?: boolean; customToken?: string; error?: string; code?: string; }
interface DemoRequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: JsonValue;
  demoApiBaseUrl?: string;
  timeoutMs?: number;
  expectSession?: boolean;
}

const LOCAL_SHARE_SERVER_PORT =
  process.env.REACT_APP_SHARE_SERVER_PORT || "3002";
const DEFAULT_PUBLIC_DEMO_ORIGIN = "https://cineparagon.com";
const DEMO_REQUEST_TIMEOUT_MS = 60 * 1000;
let demoSignInDepth = 0;

export const isDemoSignInInProgress = () => demoSignInDepth > 0;

export const getDemoApiBaseUrl = (
  location: Partial<Pick<Location, "hostname" | "port" | "origin">> | null = typeof window !== "undefined" ? window.location : null
) => {
  if (process.env.REACT_APP_SHARE_SERVER_URL) {
    return process.env.REACT_APP_SHARE_SERVER_URL.replace(/\/$/, "");
  }
  if (
    location?.hostname === "localhost" &&
    location?.port !== LOCAL_SHARE_SERVER_PORT
  ) {
    return `http://localhost:${LOCAL_SHARE_SERVER_PORT}`;
  }
  return location?.origin || "";
};

// Production already proxies `/share/*` to the share server for Open Graph
// pages. Keeping demo under that same prefix avoids sending it to the SPA
// fallback, while the local share server accepts the same endpoint directly.
const toDemoServerPath = (path: string) => String(path).replace(/^\/api\/demo(?=\/|$)/, "/share/demo");

export const getDemoRequestPaths = (
  path: string,
  location: Partial<Pick<Location, "hostname" | "port" | "origin">> | null = typeof window !== "undefined" ? window.location : null
) => {
  const apiPath = String(path);
  const sharePath = toDemoServerPath(apiPath);
  const isLocalReactDevServer =
    location?.hostname === "localhost" &&
    location?.port !== LOCAL_SHARE_SERVER_PORT;
  return isLocalReactDevServer
    ? [apiPath, sharePath]
    : [sharePath, apiPath];
};

export const getDemoRequestUrls = (
  path: string,
  location: Partial<Pick<Location, "hostname" | "port" | "origin">> | null = typeof window !== "undefined" ? window.location : null
) => {
  const baseUrl = getDemoApiBaseUrl(location);
  const urls = getDemoRequestPaths(path, location)
    .map((requestPath) => `${baseUrl}${requestPath}`);
  const isLocalReactDevServer =
    location?.hostname === "localhost" &&
    location?.port !== LOCAL_SHARE_SERVER_PORT;

  // Local development normally uses port 3002. If that optional process is
  // stopped or misconfigured, the deployed share API is a safe fallback and
  // already permits localhost via CORS.
  if (isLocalReactDevServer && !process.env.REACT_APP_SHARE_SERVER_URL) {
    urls.push(`${DEFAULT_PUBLIC_DEMO_ORIGIN}${toDemoServerPath(path)}`);
  }
  return [...new Set(urls)];
};

const fetchWithTimeout = async (
  url: string|URL|Request,
  options: RequestInit|undefined,
  timeoutMs = DEMO_REQUEST_TIMEOUT_MS
) => {
  if (typeof AbortController === "undefined") return fetch(url, options);
  const controller = new AbortController();
  const normalizedTimeoutMs = Number.isFinite(Number(timeoutMs))
    ? Math.max(1, Number(timeoutMs))
    : DEMO_REQUEST_TIMEOUT_MS;
  const timeoutId = setTimeout(() => controller.abort(), normalizedTimeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
};

const getPreferredDemoRequestUrls = (path: string, preferredBaseUrl: string | URL | undefined) => {
  if (!preferredBaseUrl) return getDemoRequestUrls(path);
  try {
    const preferredUrl = new URL(preferredBaseUrl);
    const allowedOrigins = new Set(
      getDemoRequestUrls(path).map((requestUrl) => {
        try { return new URL(requestUrl).origin; } catch { return ""; }
      })
    );
    if (!allowedOrigins.has(preferredUrl.origin)) {
      return getDemoRequestUrls(path);
    }
    const baseUrl = preferredUrl.origin;
    return getDemoRequestPaths(path, preferredUrl)
      .map((requestPath) => `${baseUrl}${requestPath}`);
  } catch {
    return getDemoRequestUrls(path);
  }
};

const requestDemoApi = async (auth: DemoRequestAuth, path: string, options: DemoRequestOptions = {}) => {
  const currentUser = auth?.currentUser;
  // Начало и возобновление демо доступны с экрана входа, где пользователя ещё
  // нет. После входа custom token остальные операции по-прежнему получают
  // обычный Firebase ID token и проверяются сервером.
  const token = currentUser ? await currentUser.getIdToken(true) : "";
  const requestOptions = {
    method: options.method || "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {})
    },
    body: options.body ? JSON.stringify(options.body) : undefined
  };
  const requestUrls = getPreferredDemoRequestUrls(
    path,
    options.demoApiBaseUrl
  );
  let successfulRequestUrl = "";
  let response: Response | null = null;
  let networkError: unknown = null;
  for (let index = 0; index < requestUrls.length; index += 1) {
    try {
      response = await fetchWithTimeout(
        requestUrls[index],
        requestOptions,
        options.timeoutMs
      );
    } catch (error) {
      networkError = error;
      response = null;
      continue;
    }
    const hasFallback = index < requestUrls.length - 1;
    if (response.status === 404 || (hasFallback && response.status >= 500)) {
      continue;
    }
    successfulRequestUrl = requestUrls[index];
    break;
  }
  if (!response) {
    const error: Error & { code?: string; status?: number } = new Error("Демо-сервер недоступен или ещё не обновлён.");
    error.code = "demo/network-error";
    error.cause = networkError;
    throw error;
  }
  const payload: DemoApiPayload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.ok === false || (options.expectSession && !payload?.customToken)) {
    const error: Error & { code?: string; status?: number } = new Error(payload?.error || "Демо-сервер недоступен или ещё не обновлён.");
    error.code = payload?.code || "demo/request-failed";
    error.status = response.status;
    throw error;
  }
  let demoApiBaseUrl = "";
  try { demoApiBaseUrl = new URL(successfulRequestUrl).origin; } catch {}
  return {
    ...payload,
    ...(demoApiBaseUrl ? { demoApiBaseUrl } : {})
  };
};

const finishDemoSignIn = async (auth: DemoAuth, payload: DemoApiPayload) => {
  if (!payload?.customToken || !isDemoUserId(payload.uid)) {
    throw new Error("Сервер вернул некорректную демо-сессию");
  }
  const existing = readDemoSession();
  const preservePendingActivity = existing?.uid === payload.uid &&
    existing.activityPending === true;
  const serverLastActiveAt = Number(payload.lastActiveAt);
  // Firebase notifies auth observers before the sign-in promise continuation
  // is guaranteed to run. Persist the already server-validated resume pointer
  // first, so the demo banner cannot see an authenticated demo with no local
  // session and immediately sign it back out.
  const savedSession = saveDemoSession({
    ...payload,
    lastActiveAt: Math.max(
      Number.isFinite(serverLastActiveAt) && serverLastActiveAt > 0
        ? serverLastActiveAt
        : Date.now(),
      preservePendingActivity ? Number(existing.lastActiveAt) || 0 : 0
    ),
    activityPending: preservePendingActivity
  });
  demoSignInDepth += 1;
  try {
    const credential = await auth.signInWithCustomToken(payload.customToken);
    if (credential?.user?.uid !== payload.uid) {
      clearDemoSession();
      throw new Error("Не удалось открыть созданную демо-сессию");
    }
  } finally {
    demoSignInDepth = Math.max(0, demoSignInDepth - 1);
  }
  const latestSession = readDemoSession();
  if (
    latestSession?.uid === payload.uid &&
    latestSession.activityPending &&
    typeof window !== "undefined" &&
    typeof window.dispatchEvent === "function"
  ) {
    // Contexts can record meaningful demo activity while Firebase is still
    // publishing the custom-token user. Replay the sync only after sign-in is
    // fully settled; an early touch would use an incomplete auth transition.
    Promise.resolve().then(() => {
      window.dispatchEvent(new CustomEvent(DEMO_ACTIVITY_EVENT, {
        detail: {
          userId: latestSession.uid,
          lastActiveAt: latestSession.lastActiveAt
        }
      }));
    });
  }
  return latestSession?.uid === payload.uid ? latestSession : savedSession;
};

// The resume endpoint uses 410 when the concrete profile/session secret is no
// longer valid. A 403 can come from an optional authenticated request during
// sign-out, while a 404 can mean that one of the local/proxy API candidates is
// not deployed yet. Neither is proof that the browser-bound demo disappeared.
const TERMINAL_DEMO_SESSION_CODES = new Set([
  "demo/expired",
  "demo/session-not-found",
  "demo/invalid-session"
]);

// Do not destroy the only browser resume secret merely because an older
// backend or a proxy returned an unclassified 410. Current servers attach a
// precise terminal code after an authoritative Firestore read.
export const isTerminalDemoSessionError = (error: unknown) =>
  isRecord(error) && error.status === 410 && typeof error.code === "string" && TERMINAL_DEMO_SESSION_CODES.has(error.code);

const getSavedDemoSessionOrThrow = () => {
  const existing = readDemoSession();
  if (existing) return existing;
  const error: Error & { code?: string; status?: number } = new Error("Сохранённая демо-сессия не найдена");
  error.code = "demo/no-saved-session";
  throw error;
};

const requestSavedDemoSession = (auth: DemoRequestAuth, existing: DemoSession, options: DemoRequestOptions = {}) =>
  requestDemoApi(
    auth,
    `/api/demo/sessions/resume/${encodeURIComponent(existing.uid)}/${encodeURIComponent(existing.sessionId)}`,
    { ...options, demoApiBaseUrl: existing.demoApiBaseUrl }
  );

export const checkSavedDemoSession = async (auth: DemoRequestAuth) => {
  const existing = readDemoSession();
  if (!existing) return null;
  try {
    const payload = await requestSavedDemoSession(auth, existing);
    return saveDemoSession({ ...existing, ...payload });
  } catch (error) {
    if (isTerminalDemoSessionError(error)) {
      clearDemoSession();
      return null;
    }
    throw error;
  }
};

export const resumeDemoSession = async (auth: DemoAuth, sessionSnapshot: DemoSession | null = null) => {
  // The login UI may already hold the validated session while an auth-state
  // observer is finishing sign-out. Restore that exact snapshot before the
  // request so a transient localStorage cleanup cannot turn "Continue demo"
  // into a missing-session error.
  const existing = sessionSnapshot
    ? saveDemoSession(sessionSnapshot)
    : getSavedDemoSessionOrThrow();
  await waitForPendingSignOut();
  try {
    return await finishDemoSignIn(
      auth,
      await requestSavedDemoSession(auth, existing, { expectSession: true })
    );
  } catch (error) {
    if (isTerminalDemoSessionError(error)) clearDemoSession();
    throw error;
  }
};

export const createDemoSession = async (auth: DemoAuth) => {
  await waitForPendingSignOut();
  return finishDemoSignIn(
    auth,
    await requestDemoApi(auth, "/api/demo/sessions", { expectSession: true })
  );
};

// Kept as a compatibility alias for callers outside the login screen. Unlike
// the old implementation, an explicit start can never silently resume or
// replace a browser's saved profile.
export const startDemoSession = createDemoSession;

export const restartDemoSession = async (auth: DemoAuth) => {
  if (!isDemoUserId(auth?.currentUser?.uid)) {
    throw new Error("Перезапуск доступен только в демо-режиме");
  }
  const existing = readDemoSession();
  return finishDemoSignIn(auth, await requestDemoApi(auth, "/api/demo/sessions/restart", {
    expectSession: true,
    demoApiBaseUrl: existing?.demoApiBaseUrl
  }));
};

export const touchDemoSession = async (auth: DemoAuth, options: { restoreMissing?: boolean; timeoutMs?: number } = {}) => {
  const authenticatedDemoId = auth?.currentUser?.uid;
  if (!isDemoUserId(authenticatedDemoId)) return null;
  if (isDemoSignInInProgress()) return null;
  const existing = readDemoSession();
  const payload = await requestDemoApi(auth, "/api/demo/sessions/touch", {
    demoApiBaseUrl: existing?.demoApiBaseUrl,
    timeoutMs: options.timeoutMs
  });
  const latestSession = readDemoSession();
  const canRestoreMissing = options.restoreMissing === true && !latestSession;
  if (
    payload?.uid === authenticatedDemoId &&
    payload?.expiresAt &&
    (latestSession?.uid === payload.uid || canRestoreMissing) &&
    (payload.sessionId || latestSession?.sessionId)
  ) {
    saveDemoSession({
      ...(latestSession || {}),
      ...payload,
      // An authenticated touch is authoritative for this demo UID and can
      // repair an older/corrupted local key before explicit logout. A rolling
      // backend that omits the key still cannot erase the browser capability.
      sessionId: payload.sessionId || latestSession?.sessionId,
      activityPending: false
    });
  }
  return payload;
};

export const exitDemoSession = async (auth: DemoAuth) => {
  clearDemoSession();
  if (auth?.currentUser) await signOutWithBarrier(auth);
};
